import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';

interface MetlifeCompleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The policy number already on the application, offered as the starting value. */
  initialMetLifeId?: string;
  isPending?: boolean;
  onConfirm: (metLifeIdNumber: string) => void | Promise<void>;
}

/** Completing an application requires the policy number issued by the insurer. */
export const MetlifeCompleteDialog = ({
  open,
  onOpenChange,
  initialMetLifeId,
  isPending = false,
  onConfirm,
}: MetlifeCompleteDialogProps) => {
  const { t } = useTranslation();
  const [metLifeId, setMetLifeId] = useState('');

  useEffect(() => {
    if (open) setMetLifeId(initialMetLifeId && initialMetLifeId !== 'N/A' ? initialMetLifeId : '');
  }, [open, initialMetLifeId]);

  const trimmed = metLifeId.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent alignCloseWithTitle className="grid-cols-[minmax(0,1fr)] sm:max-w-md">
        <DialogHeader className="pr-6">
          <DialogTitle className="break-words">{t('COMPLETE_METLIFE_APPLICATION')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="metlife-id">
            {t('METLIFE_ID')}
          </Label>
          <Input
            id="metlife-id"
            value={metLifeId}
            onChange={(event) => setMetLifeId(event.target.value)}
            placeholder={t('ENTER_METLIFE_ID')}
          />
        </div>
        <DialogFooter>
          <DialogCancelButton onClick={() => onOpenChange(false)}>{t('CANCEL')}</DialogCancelButton>
          <Button
            type="button"
            loading={isPending}
            disabled={isPending || !trimmed}
            onClick={() => onConfirm(trimmed)}
          >
            {t('CONFIRM')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
